"use client";

import {
  ProductAnnouncementsFromJSON,
  type ProductAnnouncement,
} from "@/generated/email_parser.openapi";
import { usePostHog } from "posthog-js/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { usePathname } from "next/navigation";
import ProductAnnouncementsSurface from "./ProductAnnouncementsSurface";
import {
  filterVisibleAnnouncements,
  isAnnouncementTeaserSuppressed,
} from "./productAnnouncementVisibility";

type ProductAnnouncementsContextValue = {
  latest?: ProductAnnouncement;
  hasUnread: boolean;
  openLatest: (source?: string) => void;
};

const ProductAnnouncementsContext =
  createContext<ProductAnnouncementsContextValue>({
    hasUnread: false,
    openLatest: () => undefined,
  });

export default function ProductAnnouncementsProvider({
  children,
}: PropsWithChildren) {
  const posthog = usePostHog();
  const pathname = usePathname();
  const teaserSuppressed = isAnnouncementTeaserSuppressed(pathname);
  const [items, setItems] = useState<ProductAnnouncement[]>([]);
  const [open, setOpen] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  const [featureFlagsRevision, setFeatureFlagsRevision] = useState(0);
  const visibleItems = useMemo(() => {
    void featureFlagsRevision;
    return filterVisibleAnnouncements(items, (featureFlag) =>
      posthog?.isFeatureEnabled(featureFlag)
    );
  }, [featureFlagsRevision, items, posthog]);
  const latest = visibleItems[0];

  useEffect(() => {
    if (!posthog) return;
    return posthog.onFeatureFlags(() => {
      setFeatureFlagsRevision((current) => current + 1);
    });
  }, [posthog]);

  useEffect(() => {
    const controller = new AbortController();
    void fetch("/api/user/product_announcements", { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : { items: [] }))
      // Run the wire payload through the generated codec. `response.json()` is `any`,
      // so annotating it as ProductAnnouncements typechecks while leaving publishedAt
      // an ISO *string* — which is what crashed `publishedAt.toISOString()` in prod.
      .then((json: unknown) =>
        setItems(ProductAnnouncementsFromJSON(json).items)
      )
      .catch((error: unknown) => {
        if (!(error instanceof Error && error.name === "AbortError"))
          setItems([]);
      });
    return () => controller.abort();
  }, []);

  // Shared by opening the modal and dismissing the teaser. Both are the guest
  // telling us they have seen the announcement, and both have to persist it:
  // teaser dismissal used to be local-only state, so the teaser came back on
  // every page load until the modal was opened.
  const markLatestRead = useCallback(() => {
    if (!latest || latest.isRead) return;
    const announcementId = latest.id;
    const setReadFlag = (isRead: boolean) =>
      setItems((current) =>
        current.map((item) =>
          item.id === announcementId ? { ...item, isRead } : item
        )
      );
    setReadFlag(true);
    void fetch(
      `/api/user/product_announcements/${encodeURIComponent(announcementId)}/read`,
      {
        method: "PUT",
      }
    )
      // `fetch` only rejects on network failure — a 401 from the route's session
      // check, or a 5xx from the upstream client, resolves normally. Checking
      // `ok` is what actually makes the rollback cover a failed write; the bare
      // .catch() silently left the badge marked read on every backend error.
      .then((response) => {
        if (!response.ok) setReadFlag(false);
      })
      .catch(() => {
        // Offline or DNS failure. Also keeps this from being an unhandled
        // rejection, which is what it was before the catch existed.
        setReadFlag(false);
      });
  }, [latest]);

  const openLatest = useCallback(
    (source = "unknown") => {
      if (!latest) return;
      setOpen(true);
      setTeaserDismissed(true);
      posthog?.capture("product_announcement_opened", {
        announcement_id: latest.id,
        source,
        platform: "web",
      });
      markLatestRead();
    },
    [latest, markLatestRead, posthog]
  );

  const dismissTeaser = useCallback(() => {
    setTeaserDismissed(true);
    if (latest) {
      posthog?.capture("product_announcement_dismissed", {
        announcement_id: latest.id,
        platform: "web",
      });
    }
    markLatestRead();
  }, [latest, markLatestRead, posthog]);

  const value = useMemo(
    () => ({
      latest,
      hasUnread: Boolean(latest && !latest.isRead),
      openLatest,
    }),
    [latest, openLatest, visibleItems]
  );

  return (
    <ProductAnnouncementsContext.Provider value={value}>
      {children}
      <ProductAnnouncementsSurface
        announcements={visibleItems}
        open={open}
        teaserVisible={Boolean(
          latest && !latest.isRead && !teaserDismissed && !teaserSuppressed
        )}
        onOpen={() => openLatest("teaser")}
        onOpenChange={setOpen}
        onDismissTeaser={dismissTeaser}
      />
    </ProductAnnouncementsContext.Provider>
  );
}

export function useProductAnnouncements() {
  return useContext(ProductAnnouncementsContext);
}
